import { useEffect, useState } from 'react';
import Button from '../../../components/button/Button';
import Card from '../../../components/card/Card';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/Spinner/spinner';
import P from '../../../components/texts/p/P';
import { legalEntities } from '../../../mocks/LegalEntities';
import {
  downloadClientDocument,
  downloadLufeCloudFile,
} from '../../../services/ClientService';
import { downloadApplicationFile } from '../../../services/ApplicationService';
import { documentData } from '../../../types/interfaces';
import { getMimeType } from '../../../utils/Utils';
import { getOtherLegalEntitiesFileTypes } from '../../../services/ApplicationService';

interface DocumentProps {
  documents: documentData[];
  legalEntity: string;
  cuit: string;
  applicationId?: string;
}

const SummaryDocuments: React.FC<DocumentProps> = ({
  documents,
  legalEntity,
  cuit,
  applicationId,
}) => {
  const [isSaving, setIsSaving] = useState<boolean[]>([]);
  const [otherLegalEntities, setOtherLegalEntities] = useState<Array<{id: number; code: string; description: string}>>([]);

  // Fetch de tipos de documentos al montar el componente
  useEffect(() => {
    const fetchOtherLegalEntities = async () => {
      try {
        const response = await getOtherLegalEntitiesFileTypes();
        if (response.success && response.data) {
          setOtherLegalEntities(response.data);
        }
      } catch (error) {
        console.error('Error fetching other legal entities:', error);
      }
    };

    fetchOtherLegalEntities();
  }, []);

  const handleDownloadFile = (filename: string, index: number) => {
    setIsSaving((prev) => {
      const newSavingState = [...prev];
      newSavingState[index] = true;
      return newSavingState;
    });

    if (!filename?.includes('.')) {
      downloadLufeCloudFile(cuit, filename).then((response) => {
        if (response.success) {
          const fileBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const url = window.URL.createObjectURL(fileBlob);
          window.open(url, '_blank');
        }
        setIsSaving((prev) => {
          const newSavingState = [...prev];
          newSavingState[index] = false;
          return newSavingState;
        });
      });
    } else {
      // Si hay applicationId, usar el endpoint de aplicaciones
      const downloadPromise = applicationId
        ? downloadApplicationFile(cuit, applicationId, filename)
        : downloadClientDocument(cuit, filename);
      
      downloadPromise.then((response) => {
        if (response.success) {
          const mimeType = getMimeType(filename);
          const fileBlob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(fileBlob);

          if (
            mimeType.startsWith('application/pdf') ||
            mimeType.startsWith('image/')
          ) {
            window.open(url, '_blank');
          } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
        setIsSaving((prev) => {
          const newSavingState = [...prev];
          newSavingState[index] = false;
          return newSavingState;
        });
      });
    }
  };

  return (
    <Card margin={{ top: '5' }}>
      <P type='1' weight='bold' variant='neutral'>
          Documentación
        </P>
      <Card
        display='flex'
        flexWrap='wrap'
        gap='20px'
        justifyContent='space-between'
        margin={{ top: '3' }}
      >
        {legalEntities
          .find((item) => item.label === legalEntity)
          ?.requiredDocuments.map((document, index) =>
            documents.find((item) => item?.type === document.id)?.type &&
            documents.find((item) => item?.type === document.id)
              ?.is_basic_data ? (
              <Card
                key={index}
                width='48%'
                display='flex'
                height='auto'
                alignItems='center'
                gap='16px'
              >
                <Icon name='done_outline' variant='success' />
                <Card width='-webkit-fill-available'>
                  <P withMargin={false} type='2' variant='neutral' align='left'>
                    {' '}
                    {document.value}
                  </P>
                </Card>
                <Card display='flex' justifyContent='center' width='75px'>
                  {isSaving[index] ? (
                    <Spinner size='md' variant='primary' />
                  ) : (
                    <Button
                      variant='secondary'
                      size='md'
                      onClick={() => {
                        const filename = documents.find(
                          (item) => item?.type === document.id
                        )?.file_name;
                        if (filename) {
                          handleDownloadFile(filename, index);
                        }
                      }}
                    >
                      <Icon
                        name='view_outline'
                        height={44}
                        variant='neutral'
                        cursor='pointer'
                      />
                    </Button>
                  )}
                </Card>
              </Card>
            ) : (
              <Card
                key={index}
                width='48%'
                display='flex'
                height='40px'
                alignItems='center'
                gap='16px'
              >
                <Icon name='cancel_outline' variant='danger' />
                <Card width='-webkit-fill-available'>
                  <P
                    withMargin={false}
                    type='2'
                    variant='neutral'
                    align='left'
                    modifier='weak'
                  >
                    {' '}
                    {document.value}
                  </P>
                </Card>
              </Card>
            )
          )}
      </Card>
      {documents.some((doc) => {
        // Excluir documentos de instrumentación (013-018) de la documentación extra
        const excludedTypes = ['013', '014', '015', '016', '017', '018'];
        return !doc.is_basic_data && !excludedTypes.includes(doc.type);
      }) && (
        <Card margin={{ top: '5' }}>
          <P type='1' weight='bold' variant='neutral'>
            Documentación extra
          </P>
          <Card
            display='flex'
            flexWrap='wrap'
            gap='20px'
            justifyContent='space-between'
          >
            {documents.map(
              (document, index) => {
                // Excluir documentos de instrumentación (013-018) de la documentación extra
                const excludedTypes = ['013', '014', '015', '016', '017', '018'];
                return !document.is_basic_data && !excludedTypes.includes(document.type) && (
                  <Card
                    key={index}
                    width='48%'
                    display='flex'
                    alignItems='center'
                    gap='16px'
                    margin={{ bottom: '0' }}
                  >
                    <Icon name='done_outline' variant='success' />
                    <Card width='-webkit-fill-available'>
                      <P
                        withMargin={false}
                        type='2'
                        variant='neutral'
                        align='left'
                      >
                        {
                          otherLegalEntities.find(
                            (item) => item.code === document.type
                          )?.description
                        }
                      </P>
                    </Card>
                    <Card display='flex' justifyContent='center' width='75px'>
                      {isSaving[index] ? (
                        <Spinner size='md' variant='primary' />
                      ) : (
                        <Button
                          variant='secondary'
                          size='md'
                          onClick={() =>
                            handleDownloadFile(document.file_name, index)
                          }
                        >
                          <Icon
                            name='view_outline'
                            height={44}
                            variant='neutral'
                            cursor='pointer'
                          />
                        </Button>
                      )}
                    </Card>
                  </Card>
                );
              }
            )}
          </Card>
        </Card>
      )}
    </Card>
  );
};

export default SummaryDocuments;
